import { useState, useEffect, useCallback, useRef } from "react";
import type { Socket } from "socket.io-client";

// ─── Types ──────────────────────────────────────────────────────
interface MarketSummary {
  resource: string;
  lastPrice: number;
  bestBid: number;
  bestAsk: number;
  volume24h: number;
  change24h?: number;
  prices?: number[];
}

interface OrderBookEntry { price: number; quantity: number; count: number; }
interface Trade { _id: string; resource: string; price: number; quantity: number; buyerName: string; sellerName: string; timestamp: string; }
interface PriceCandle { timestamp: string; open: number; high: number; low: number; close: number; volume: number; }
interface MyOrder {
  _id: string; type: "buy" | "sell"; resource: string; price: number;
  quantity: number; filled: number; status: string; createdAt: string; expiresAt: string;
}

// ─── Constants ──────────────────────────────────────────────────
const RESOURCES = [
  { id: "steel", name: "Steel", emoji: "🧱", code: "FE-400" },
  { id: "fuel", name: "Fuel", emoji: "⛽", code: "RP-1" },
  { id: "electronics", name: "Electronics", emoji: "📟", code: "CIRC-A" },
  { id: "munitions", name: "Munitions", emoji: "💣", code: "HE-CHG" },
  { id: "uranium", name: "Uranium", emoji: "☢️", code: "U-235" },
  { id: "rareEarth", name: "Rare Earth", emoji: "💎", code: "NEO-X" },
  { id: "composites", name: "Composites", emoji: "🛠", code: "KVL-F" },
];

const MS = ({ icon, size = 20 }: { icon: string; size?: number }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size }}>{icon}</span>
);

const fmt = (n: number) => n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + "M" : n >= 1_000 ? (n / 1_000).toFixed(1) + "K" : n.toFixed(2);
const fmtPrice = (n: number) => n == null ? "—" : "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Sparkline SVG ──────────────────────────────────────────────
function Sparkline({ prices, color }: { prices: number[]; color: string }) {
  if (!prices || prices.length < 2) return <div className="w-16 h-8" />;
  const min = Math.min(...prices), max = Math.max(...prices);
  const range = max - min || 1;
  const pts = prices.map((p, i) => `${(i / (prices.length - 1)) * 100},${40 - ((p - min) / range) * 36}`).join(" L");
  return (
    <svg className="w-16 h-8" viewBox="0 0 100 40">
      <path d={`M${pts}`} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Mini Candlestick Chart ─────────────────────────────────────
function CandleChart({ candles }: { candles: PriceCandle[] }) {
  if (!candles || candles.length === 0) return <div className="h-[200px] flex items-center justify-center text-zinc-600 text-xs">NO DATA</div>;
  const allPrices = candles.flatMap(c => [c.high, c.low]);
  const min = Math.min(...allPrices), max = Math.max(...allPrices);
  const range = max - min || 1;
  const h = 200;
  const toY = (p: number) => h - ((p - min) / range) * (h - 20) - 10;
  const w = Math.max(8, Math.floor(300 / candles.length));

  return (
    <svg className="w-full" viewBox={`0 0 ${candles.length * w} ${h}`} preserveAspectRatio="none">
      {candles.map((c, i) => {
        const bull = c.close >= c.open;
        const color = bull ? "#4bee74" : "#d53d18";
        const bodyTop = toY(Math.max(c.open, c.close));
        const bodyBot = toY(Math.min(c.open, c.close));
        const bodyH = Math.max(1, bodyBot - bodyTop);
        return (
          <g key={i}>
            <line x1={i * w + w / 2} x2={i * w + w / 2} y1={toY(c.high)} y2={toY(c.low)} stroke={color} strokeWidth="1" />
            <rect x={i * w + 1} y={bodyTop} width={w - 2} height={bodyH} fill={color} rx="1" />
          </g>
        );
      })}
    </svg>
  );
}

// ─── Order Book Depth ───────────────────────────────────────────
function OrderBookPanel({ buys, sells }: { buys: OrderBookEntry[]; sells: OrderBookEntry[] }) {
  const maxQty = Math.max(...buys.map(b => b.quantity), ...sells.map(s => s.quantity), 1);
  return (
    <div className="grid grid-cols-2 divide-x divide-zinc-800 text-xs">
      <div>
        <div className="flex justify-between px-3 py-1.5 text-[9px] text-zinc-500 uppercase tracking-widest bg-zinc-900/50">
          <span>Price</span><span>Qty</span>
        </div>
        {buys.slice(0, 6).map((b, i) => (
          <div key={i} className="relative h-7 flex items-center justify-between px-3">
            <div className="absolute inset-y-0 right-0 bg-green-500/10" style={{ width: `${(b.quantity / maxQty) * 100}%` }} />
            <span className="text-green-400 font-medium z-10">{b.price.toFixed(2)}</span>
            <span className="text-zinc-400 z-10">{fmt(b.quantity)}</span>
          </div>
        ))}
        {buys.length === 0 && <div className="px-3 py-4 text-zinc-600 text-center">No bids</div>}
      </div>
      <div>
        <div className="flex justify-between px-3 py-1.5 text-[9px] text-zinc-500 uppercase tracking-widest bg-zinc-900/50">
          <span>Price</span><span>Qty</span>
        </div>
        {sells.slice(0, 6).map((s, i) => (
          <div key={i} className="relative h-7 flex items-center justify-between px-3">
            <div className="absolute inset-y-0 left-0 bg-red-500/10" style={{ width: `${(s.quantity / maxQty) * 100}%` }} />
            <span className="text-red-400 font-medium z-10">{s.price.toFixed(2)}</span>
            <span className="text-zinc-400 z-10">{fmt(s.quantity)}</span>
          </div>
        ))}
        {sells.length === 0 && <div className="px-3 py-4 text-zinc-600 text-center">No asks</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function MarketplacePage({ socket, player }: { socket: Socket | null; player: any }) {
  // ── State ──
  const [view, setView] = useState<"overview" | "detail" | "orders">("overview");
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [summaries, setSummaries] = useState<MarketSummary[]>([]);
  const [orderBook, setOrderBook] = useState<{ buys: OrderBookEntry[]; sells: OrderBookEntry[] }>({ buys: [], sells: [] });
  const [trades, setTrades] = useState<Trade[]>([]);
  const [candles, setCandles] = useState<PriceCandle[]>([]);
  const [myOrders, setMyOrders] = useState<MyOrder[]>([]);
  const [ordersTab, setOrdersTab] = useState<"open" | "filled" | "cancelled">("open");

  // Place order form
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderType, setOrderType] = useState<"buy" | "sell">("buy");
  const [orderQty, setOrderQty] = useState(100);
  const [orderPrice, setOrderPrice] = useState(0);
  const [orderResource, setOrderResource] = useState("steel");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<any>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Socket listeners ──
  useEffect(() => {
    if (!socket) return;
    const onSummary = (d: any) => setSummaries(d.summary || []);
    const onOrderBook = (d: any) => setOrderBook({ buys: d.buys || [], sells: d.sells || [] });
    const onTrades = (d: any) => setTrades(d.trades || []);
    const onHistory = (d: any) => setCandles(d.history || []);
    const onMyOrders = (d: any) => setMyOrders(d.orders || []);
    const onPlaced = (d: any) => {
      setPlacing(false);
      if (d.error) { setError(d.error); return; }
      setShowOrderModal(false);
      showToast(`Order placed: ${d.order?.type?.toUpperCase()} ${d.order?.quantity} ${d.order?.resource} @ ${fmtPrice(d.order?.price)}`);
      socket.emit("market-summary");
      socket.emit("market-my-orders");
      if (selectedResource) { socket.emit("market-orderbook", { resource: selectedResource }); socket.emit("market-trades", { resource: selectedResource }); }
    };
    const onCancelled = (d: any) => {
      if (d.error) { showToast("Cancel failed: " + d.error); return; }
      showToast(`Order cancelled. Refund: ${fmtPrice(d.refund?.amount || 0)}`);
      socket.emit("market-my-orders");
      socket.emit("market-summary");
    };
    const onFilled = (d: any) => {
      showToast(`Trade filled: ${d.type?.toUpperCase()} ${d.quantity} ${d.resource} @ ${fmtPrice(d.price)}`);
      socket.emit("market-my-orders");
      socket.emit("market-summary");
    };
    const onPriceUpdate = (d: any) => {
      setSummaries(d.summary || []);
      if (selectedResource) { socket.emit("market-orderbook", { resource: selectedResource }); socket.emit("market-trades", { resource: selectedResource }); }
    };
    const onError = (d: any) => { setPlacing(false); setError(d.error || "Unknown error"); };

    socket.on("market-summary", onSummary);
    socket.on("market-orderbook", onOrderBook);
    socket.on("market-trades", onTrades);
    socket.on("market-history", onHistory);
    socket.on("market-my-orders", onMyOrders);
    socket.on("market-order-placed", onPlaced);
    socket.on("market-order-cancelled", onCancelled);
    socket.on("market-trade-filled", onFilled);
    socket.on("market-price-update", onPriceUpdate);
    socket.on("market-error", onError);

    // Initial data load
    socket.emit("market-summary");
    socket.emit("market-my-orders");

    return () => {
      socket.off("market-summary", onSummary);
      socket.off("market-orderbook", onOrderBook);
      socket.off("market-trades", onTrades);
      socket.off("market-history", onHistory);
      socket.off("market-my-orders", onMyOrders);
      socket.off("market-order-placed", onPlaced);
      socket.off("market-order-cancelled", onCancelled);
      socket.off("market-trade-filled", onFilled);
      socket.off("market-price-update", onPriceUpdate);
      socket.off("market-error", onError);
    };
  }, [socket, selectedResource, showToast]);

  // ── Helpers ──
  const openDetail = (resId: string) => {
    setSelectedResource(resId);
    setView("detail");
    socket?.emit("market-orderbook", { resource: resId });
    socket?.emit("market-trades", { resource: resId });
    socket?.emit("market-history", { resource: resId });
  };

  const openOrderModal = (type: "buy" | "sell", resId?: string) => {
    const res = resId || selectedResource || "steel";
    setOrderResource(res);
    setOrderType(type);
    const summary = summaries.find(s => s.resource === res);
    setOrderPrice(type === "buy" ? (summary?.bestAsk || summary?.lastPrice || 1) : (summary?.bestBid || summary?.lastPrice || 1));
    setOrderQty(100);
    setError(null);
    setShowOrderModal(true);
  };

  const placeOrder = () => {
    if (!socket || placing) return;
    setPlacing(true);
    setError(null);
    socket.emit("market-place-order", { type: orderType, resource: orderResource, quantity: orderQty, price: orderPrice });
  };

  const cancelOrder = (orderId: string) => { socket?.emit("market-cancel-order", { orderId }); };

  const listingFee = orderQty * orderPrice * 0.02;
  const txFee = orderType === "sell" ? orderQty * orderPrice * 0.05 : 0;
  const totalCost = orderType === "buy" ? orderQty * orderPrice + listingFee : listingFee;

  const resInfo = (id: string) => RESOURCES.find(r => r.id === id) || { name: id, emoji: "📦", code: "UNK" };
  const openOrders = myOrders.filter(o => o.status === "open");
  const filledOrders = myOrders.filter(o => o.status === "filled");
  const cancelledOrders = myOrders.filter(o => o.status === "cancelled" || o.status === "expired");

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="ip-slide-up ip-panels-top fixed bottom-14 left-0 right-0 bg-[#090e19] z-40 flex flex-col overflow-hidden" style={{ maxHeight: "calc(100vh - 3.5rem)" }}>
      {/* Toast */}
      {toast && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[100] bg-green-900/90 border border-green-500/30 text-green-300 px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-xl shadow-lg animate-in slide-in-from-top duration-300">
          ✅ {toast}
        </div>
      )}

      {/* Header */}
      <header className="bg-[#0e131f] border-b border-orange-500/10 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          {view !== "overview" && (
            <button onClick={() => { setView("overview"); setSelectedResource(null); }} className="text-orange-400 bg-orange-500/10 rounded-lg px-2 py-1.5 flex items-center gap-1 active:scale-90 transition-all">
              <MS icon="arrow_back" /> <span className="text-xs font-bold tracking-wider">BACK</span>
            </button>
          )}
          <div>
            <h1 className="text-orange-500 font-black tracking-tighter text-lg font-headline">TACTICAL_EXCHANGE</h1>
            {view === "detail" && selectedResource && (
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">ASSET: {resInfo(selectedResource).code}</span>
            )}
          </div>
        </div>
        <div className="bg-zinc-800/50 px-3 py-1 rounded text-xs">
          <span className="text-zinc-500 mr-1">CR</span>
          <span className="text-orange-400 font-bold font-headline">{fmtPrice(player?.resources?.money || 0)}</span>
        </div>
      </header>

      {/* Sub-nav */}
      <div className="flex border-b border-zinc-800 bg-[#0e131f] shrink-0">
        {[
          { id: "overview" as const, label: "MARKET", icon: "grid_view" },
          { id: "orders" as const, label: "ORDERS", icon: "receipt_long" },
        ].map(t => (
          <button key={t.id} onClick={() => { setView(t.id); if (t.id === "orders") socket?.emit("market-my-orders"); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold tracking-widest uppercase transition-all ${view === t.id || (view === "detail" && t.id === "overview") ? "text-orange-500 border-b-2 border-orange-500" : "text-zinc-500"}`}>
            <MS icon={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-4">

        {/* ════════════ OVERVIEW ════════════ */}
        {view === "overview" && (
          <div className="px-3 pt-3">
            {/* Ticker banner */}
            {summaries.length > 0 && (
              <div className="overflow-hidden bg-zinc-900/50 rounded mb-3 py-1.5 px-3">
                <div className="flex gap-6 text-[10px] text-zinc-500 uppercase tracking-widest whitespace-nowrap overflow-x-auto scrollbar-hide">
                  {summaries.map(s => {
                    const ch = (s.change24h || 0);
                    return (
                      <span key={s.resource}>
                        {resInfo(s.resource).name} {ch >= 0 ? "+" : ""}{ch.toFixed(1)}%{" "}
                        <span className={ch > 0 ? "text-green-400" : ch < 0 ? "text-red-400" : "text-zinc-600"}>{ch > 0 ? "▲" : ch < 0 ? "▼" : "●"}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resource list */}
            <div className="space-y-1">
              {RESOURCES.map(res => {
                const s = summaries.find(x => x.resource === res.id);
                const ch = s?.change24h || 0;
                return (
                  <button key={res.id} onClick={() => openDetail(res.id)}
                    className="w-full bg-[#0e131f] hover:bg-[#131927] transition-all px-4 py-3.5 flex items-center justify-between border-l-2 border-transparent hover:border-orange-500 active:scale-[0.98]">
                    <div className="flex items-center gap-3 w-1/3">
                      <div className="w-9 h-9 flex items-center justify-center bg-zinc-800/50 rounded text-lg">{res.emoji}</div>
                      <div className="text-left">
                        <h3 className="font-headline font-bold text-sm text-zinc-100 leading-tight">{res.name}</h3>
                        <p className="text-[9px] text-zinc-600 tracking-tighter">{res.code}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-headline font-bold text-zinc-100">{fmtPrice(s?.lastPrice || 0)}</span>
                      <div className={`flex items-center justify-end gap-0.5 text-[10px] font-bold ${ch > 0 ? "text-green-400" : ch < 0 ? "text-red-400" : "text-zinc-600"}`}>
                        {ch !== 0 && <MS icon={ch > 0 ? "arrow_upward" : "arrow_downward"} size={12} />}
                        {ch !== 0 ? `${Math.abs(ch).toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-center w-16">
                      <span className="text-[9px] text-zinc-600 uppercase">Vol</span>
                      <span className="text-[11px] font-mono text-zinc-400">{fmt(s?.volume24h || 0)}</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <Sparkline prices={s?.prices || []} color={ch >= 0 ? "#4bee74" : "#d53d18"} />
                      <div className="text-[8px] font-mono text-zinc-700 flex gap-2 mt-0.5">
                        <span>B:{(s?.bestBid || 0).toFixed(0)}</span>
                        <span>A:{(s?.bestAsk || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ════════════ RESOURCE DETAIL ════════════ */}
        {view === "detail" && selectedResource && (() => {
          const res = resInfo(selectedResource);
          const s = summaries.find(x => x.resource === selectedResource);
          const ch = s?.change24h || 0;
          const spread = (s?.bestAsk || 0) - (s?.bestBid || 0);
          return (
            <div className="px-3 pt-3 space-y-3">
              {/* Price + chart */}
              <div className="bg-[#0e131f] p-4 rounded-lg border border-zinc-800/50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">MARKET PRICE / {res.name.toUpperCase()}</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-headline font-bold text-green-300">{fmtPrice(s?.lastPrice || 0)}</span>
                      <span className={`text-sm font-medium ${ch >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {ch >= 0 ? "+" : ""}{ch.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="px-2 py-0.5 bg-zinc-800/50 rounded text-[9px] text-zinc-500 uppercase">7D</span>
                    <span className="px-2 py-0.5 bg-orange-500/10 text-orange-400 rounded text-[9px] uppercase border border-orange-500/20">LIVE</span>
                  </div>
                </div>
                <div className="h-[200px] w-full">
                  <CandleChart candles={candles} />
                </div>
              </div>

              {/* Order book */}
              <div className="bg-[#0e131f] rounded-lg border border-zinc-800/50 overflow-hidden">
                <div className="px-4 py-2.5 bg-zinc-900/50 border-b border-zinc-800/50 flex justify-between items-center">
                  <h2 className="font-headline font-bold text-xs tracking-widest uppercase">ORDER BOOK</h2>
                  <div className="text-[9px] text-zinc-500">SPREAD: <span className="text-zinc-300">{fmtPrice(spread)}</span></div>
                </div>
                <OrderBookPanel buys={orderBook.buys} sells={orderBook.sells} />
                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-2 p-3 border-t border-zinc-800/50">
                  <div className="text-center"><div className="text-[8px] text-zinc-600 uppercase">24H HIGH</div><div className="text-[11px] font-bold">{fmtPrice(candles.length ? Math.max(...candles.map(c => c.high)) : 0)}</div></div>
                  <div className="text-center"><div className="text-[8px] text-zinc-600 uppercase">24H LOW</div><div className="text-[11px] font-bold">{fmtPrice(candles.length ? Math.min(...candles.map(c => c.low)) : 0)}</div></div>
                  <div className="text-center"><div className="text-[8px] text-zinc-600 uppercase">VOLUME</div><div className="text-[11px] font-bold">{fmt(s?.volume24h || 0)}</div></div>
                  <div className="text-center"><div className="text-[8px] text-zinc-600 uppercase">BEST BID</div><div className="text-[11px] font-bold text-green-400">{fmtPrice(s?.bestBid || 0)}</div></div>
                </div>
              </div>

              {/* Recent trades */}
              <div className="bg-[#0e131f] rounded-lg border border-zinc-800/50 overflow-hidden">
                <div className="px-4 py-2.5 bg-zinc-900/50 border-b border-zinc-800/50 flex justify-between items-center">
                  <h2 className="font-headline font-bold text-xs tracking-widest uppercase">RECENT TRADES</h2>
                  <span className="text-[9px] text-green-400 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> LIVE</span>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {trades.length === 0 && <div className="py-6 text-center text-zinc-600 text-xs">No trades yet</div>}
                  {trades.slice(0, 20).map((t, i) => (
                    <div key={t._id || i} className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/30 text-xs">
                      <span className="text-zinc-500 w-16">{new Date(t.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className={t.buyerName === player?.name ? "text-green-400" : "text-red-400"}>{fmtPrice(t.price)}</span>
                      <span className="text-zinc-300">{t.quantity}</span>
                      <span className="text-zinc-600 text-[10px] truncate max-w-[80px]">{t.buyerName === player?.name ? t.sellerName : t.buyerName}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buy/Sell buttons */}
              <div className="flex gap-3 pb-4">
                <button onClick={() => openOrderModal("sell", selectedResource)}
                  className="flex-1 py-3.5 bg-zinc-800/50 border border-red-500/20 text-red-400 font-headline font-bold tracking-widest rounded-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                  <MS icon="sell" size={18} /> SELL
                </button>
                <button onClick={() => openOrderModal("buy", selectedResource)}
                  className="flex-1 py-3.5 bg-gradient-to-r from-green-600 to-green-500 text-black font-headline font-bold tracking-widest rounded-lg shadow-[0_0_20px_rgba(74,238,116,0.2)] active:scale-95 transition-all flex items-center justify-center gap-2">
                  <MS icon="shopping_cart" size={18} /> BUY
                </button>
              </div>
            </div>
          );
        })()}

        {/* ════════════ MY ORDERS ════════════ */}
        {view === "orders" && (
          <div className="px-3 pt-3">
            {/* Tabs */}
            <div className="flex border-b border-zinc-800 mb-3">
              {(["open", "filled", "cancelled"] as const).map(tab => (
                <button key={tab} onClick={() => setOrdersTab(tab)}
                  className={`px-6 py-2.5 text-xs font-bold tracking-widest uppercase transition-all ${ordersTab === tab ? "text-orange-500 border-b-2 border-orange-500" : "text-zinc-500"}`}>
                  {tab} {tab === "open" ? `(${openOrders.length})` : ""}
                </button>
              ))}
            </div>

            {/* Order list */}
            <div className="space-y-2">
              {(ordersTab === "open" ? openOrders : ordersTab === "filled" ? filledOrders : cancelledOrders).map(order => {
                const res = resInfo(order.resource);
                const fillPct = order.quantity > 0 ? (order.filled / order.quantity) * 100 : 0;
                const remaining = order.expiresAt ? Math.max(0, new Date(order.expiresAt).getTime() - Date.now()) : 0;
                const hours = Math.floor(remaining / 3600000);
                const mins = Math.floor((remaining % 3600000) / 60000);
                return (
                  <div key={order._id} className="bg-[#0e131f] p-4 rounded-lg border border-zinc-800/50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`px-2 py-0.5 rounded text-[10px] font-black tracking-widest ${order.type === "buy" ? "bg-green-500/10 border border-green-500/30 text-green-400" : "bg-red-500/10 border border-red-500/30 text-red-400"}`}>
                        {order.type.toUpperCase()}
                      </div>
                      <span className="text-sm font-bold">{res.emoji} {res.name}</span>
                      <span className="text-[9px] text-zinc-600 ml-auto">{res.code}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div><span className="text-[9px] text-zinc-600 uppercase tracking-widest">Price</span><div className="font-headline font-bold">{fmtPrice(order.price)}</div></div>
                      <div><span className="text-[9px] text-zinc-600 uppercase tracking-widest">Quantity</span><div className="font-headline font-bold">{order.quantity}</div></div>
                    </div>
                    {/* Fill bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-[9px] mb-1">
                        <span className="text-zinc-600 uppercase tracking-widest">Fill Status</span>
                        <span className="text-orange-400 font-bold">{order.filled} / {order.quantity}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full transition-all" style={{ width: `${fillPct}%` }} />
                      </div>
                    </div>
                    {order.status === "open" && (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[9px] text-zinc-600 flex items-center gap-1"><MS icon="schedule" size={12} /> {hours}h {mins}m remaining</span>
                        <button onClick={() => cancelOrder(order._id)} className="text-[10px] text-red-400 border border-red-500/20 px-3 py-1 rounded hover:bg-red-500/10 active:scale-95 transition-all font-bold tracking-widest">
                          CANCEL
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {(ordersTab === "open" ? openOrders : ordersTab === "filled" ? filledOrders : cancelledOrders).length === 0 && (
                <div className="py-12 text-center text-zinc-600 text-xs uppercase tracking-widest">No {ordersTab} orders</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ════════════ PLACE ORDER MODAL ════════════ */}
      {showOrderModal && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowOrderModal(false)}>
          <div className="bg-[#0a0f1a] w-full max-w-lg rounded-t-xl sm:rounded-xl border border-orange-500/20 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="p-5 border-b border-zinc-800 flex justify-between items-center">
              <div>
                <h2 className="font-headline font-bold text-lg">PLACE ORDER</h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[9px] text-zinc-500 uppercase tracking-widest">MARKET ONLINE</span>
                </div>
              </div>
              <button onClick={() => setShowOrderModal(false)} className="text-zinc-500 hover:text-orange-400"><MS icon="close" /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Buy/Sell toggle */}
              <div className="bg-zinc-900 p-1 rounded-lg flex">
                <button onClick={() => setOrderType("buy")}
                  className={`flex-1 py-2.5 text-sm font-bold tracking-widest rounded-md transition-all ${orderType === "buy" ? "bg-green-600 text-black shadow-[0_0_15px_rgba(74,238,116,0.2)]" : "text-zinc-500 hover:text-zinc-300"}`}>
                  BUY
                </button>
                <button onClick={() => setOrderType("sell")}
                  className={`flex-1 py-2.5 text-sm font-bold tracking-widest rounded-md transition-all ${orderType === "sell" ? "bg-red-600 text-white shadow-[0_0_15px_rgba(214,61,24,0.2)]" : "text-zinc-500 hover:text-zinc-300"}`}>
                  SELL
                </button>
              </div>

              {/* Resource selector */}
              <div className="bg-[#1a2233] p-3 rounded-lg flex justify-between items-center border-l-4 border-orange-500">
                <div>
                  <span className="text-[9px] text-orange-400 tracking-widest uppercase font-bold">ASSET</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xl">{resInfo(orderResource).emoji}</span>
                    <select value={orderResource} onChange={e => { setOrderResource(e.target.value); const s = summaries.find(x => x.resource === e.target.value); setOrderPrice(orderType === "buy" ? (s?.bestAsk || s?.lastPrice || 1) : (s?.bestBid || s?.lastPrice || 1)); }}
                      className="bg-transparent text-lg font-headline font-bold border-none focus:ring-0 p-0 text-zinc-100">
                      {RESOURCES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[9px] text-zinc-600 uppercase tracking-widest">MARKET PRICE</span>
                  <div className="text-lg font-headline font-bold text-green-300">{fmtPrice(summaries.find(x => x.resource === orderResource)?.lastPrice || 0)}</div>
                </div>
              </div>

              {/* Qty + Price inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold block mb-1.5">QUANTITY</label>
                  <div className="flex bg-zinc-800/50 rounded border border-zinc-700/50 focus-within:border-orange-500/50 overflow-hidden">
                    <button onClick={() => setOrderQty(Math.max(1, orderQty - 10))} className="px-3 py-2 hover:bg-zinc-700/50 text-zinc-400">−</button>
                    <input type="number" value={orderQty} onChange={e => setOrderQty(Math.max(1, parseInt(e.target.value) || 0))}
                      className="w-full bg-transparent border-none text-center font-headline font-bold text-base focus:ring-0" />
                    <button onClick={() => setOrderQty(orderQty + 10)} className="px-3 py-2 hover:bg-zinc-700/50 text-zinc-400">+</button>
                  </div>
                </div>
                <div>
                  <label className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold block mb-1.5">PRICE PER UNIT</label>
                  <div className="relative bg-zinc-800/50 rounded border border-zinc-700/50 focus-within:border-orange-500/50 overflow-hidden">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-bold">$</span>
                    <input type="number" value={orderPrice} onChange={e => setOrderPrice(Math.max(0.1, parseFloat(e.target.value) || 0))} step="0.01"
                      className="w-full bg-transparent border-none pl-7 pr-3 py-2 font-headline font-bold text-base focus:ring-0" />
                  </div>
                </div>
              </div>

              {/* Cost breakdown */}
              <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800/50 space-y-2.5">
                <div className="flex justify-between text-xs"><span className="text-zinc-500">Subtotal</span><span className="font-bold">{fmtPrice(orderQty * orderPrice)}</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-zinc-500">Listing Fee (2%)</span><span className="text-red-400">{fmtPrice(listingFee)}</span></div>
                {orderType === "sell" && <div className="flex justify-between text-[11px]"><span className="text-zinc-500">Transaction Fee (5%)</span><span className="text-red-400">{fmtPrice(txFee)}</span></div>}
                <div className="pt-2.5 border-t border-zinc-800 flex justify-between">
                  <span className="text-sm font-bold uppercase tracking-widest">{orderType === "buy" ? "TOTAL COST" : "LISTING COST"}</span>
                  <span className="text-lg font-headline font-bold text-orange-400">{fmtPrice(totalCost)}</span>
                </div>
              </div>

              {/* Available balance */}
              <div className="flex items-center gap-2 px-1">
                <MS icon="account_balance_wallet" size={18} />
                <div>
                  <div className="text-[8px] text-zinc-600 uppercase tracking-widest">
                    {orderType === "buy" ? "AVAILABLE CREDITS" : `AVAILABLE ${resInfo(orderResource).name.toUpperCase()}`}
                  </div>
                  <span className="text-sm font-headline font-bold">
                    {orderType === "buy" ? fmtPrice(player?.resources?.money || 0) : (player?.resources?.[orderResource] || 0).toLocaleString()}
                  </span>
                </div>
                {((orderType === "buy" && (player?.resources?.money || 0) >= totalCost) || (orderType === "sell" && (player?.resources?.[orderResource] || 0) >= orderQty)) && (
                  <span className="ml-auto text-[9px] text-green-400 font-bold flex items-center gap-1"><MS icon="check_circle" size={14} /> OK</span>
                )}
              </div>

              {/* Error */}
              {error && <div className="bg-red-900/30 border border-red-500/30 text-red-300 text-xs px-3 py-2 rounded">{error}</div>}

              {/* Submit */}
              <button onClick={placeOrder} disabled={placing}
                className={`w-full py-4 rounded-lg font-headline font-black text-lg tracking-widest uppercase active:scale-95 transition-all ${
                  orderType === "buy"
                    ? "bg-gradient-to-r from-green-600 to-green-500 text-black shadow-[0_8px_24px_rgba(74,238,116,0.2)]"
                    : "bg-gradient-to-r from-red-700 to-red-600 text-white shadow-[0_8px_24px_rgba(214,61,24,0.2)]"
                } ${placing ? "opacity-50" : ""}`}>
                {placing ? "PLACING..." : `PLACE ${orderType.toUpperCase()} ORDER`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB for new order (only on overview/orders views) */}
      {view !== "detail" && !showOrderModal && (
        <button onClick={() => openOrderModal("buy")}
          className="absolute bottom-4 right-4 w-12 h-12 bg-gradient-to-tr from-orange-600 to-orange-400 text-black rounded-full flex items-center justify-center shadow-[0_8px_24px_rgba(255,122,49,0.3)] active:scale-90 transition-all z-40">
          <MS icon="add" size={28} />
        </button>
      )}
    </div>
  );
}
